import React, {
	ReactNode,
	useCallback,
	useEffect,
	useRef,
	useState,
} from 'react';
import classNames from 'classnames';
import DraggableListDragLayer from '@client/components/DraggableList/private/DraggableListDragLayer';
import DraggableListItem, { DragItem } from '@client/components/DraggableList/private/DraggableListItem';
import styles from './DraggableList.module.css';

type RenderListItem<Data> = (
	item: Data,
	index: number,
	allItems: Data[],
	draggingItem: Data | undefined,
) => ReactNode;

type DraggableListProps<Data> = {
	dataItems: Data[];
	renderItem: RenderListItem<Data>;
	renderSeparator?: RenderListItem<Data> | null;
	itemKeyProp: keyof Data;
	onOrderChange: (items: Data[], movedItem: Data, index: number) => void;
	getCanDrag?: (data: Data, index: number, dragItem: DragItem<Data>) => boolean;
	className?: string;
};

const DraggableList = <Data, >({
	dataItems,
	renderItem,
	renderSeparator,
	itemKeyProp,
	onOrderChange,
	getCanDrag,
	className,
}: DraggableListProps<Data>) => {
	const [dragItems, setDragItems] = useState<DragItem<Data>[]>([]);

	const dragItemsToDataItems = useCallback(
		(dragItemsLocal: DragItem<Data>[]) => dragItemsLocal.map((item) => item.data),
		[],
	);

	const dataItemsToDragItems = useCallback(
		(itemsLocal: Data[]) => itemsLocal.map((item, i) => ({
			key: String(item[itemKeyProp]),
			data: item,
			index: i,
		})),
		[itemKeyProp],
	);

	useEffect(() => {
		setDragItems(dataItemsToDragItems(dataItems));
	}, [dataItems, dataItemsToDragItems]);

	const itemsContainerRef = useRef<HTMLDivElement>(null);

	const moveItem = useCallback((dragIndex: number, hoverIndex: number) => {
		const draggedItem = dragItems[dragIndex];

		const newDragItems = [...dragItems];

		newDragItems.splice(dragIndex, 1);
		newDragItems.splice(hoverIndex, 0, draggedItem);

		setDragItems(newDragItems);
	}, [dragItems]);

	const draggingDragItem = dragItems.find((di) => di.isDragging);
	const allDataItems = dragItemsToDataItems(dragItems);
	const draggingData = draggingDragItem?.data;

	return (
		<div
			className={classNames(styles.itemsContainer, className)}
			ref={itemsContainerRef}
		>
			<DraggableListDragLayer<Data>
				width={itemsContainerRef.current?.offsetWidth ?? 0}
				renderItem={(item, i) => renderItem(item, i, allDataItems, draggingData)}
			/>
			{dragItems.map((dragItem, index) => (
				<DraggableListItem<Data>
					key={dragItem.key}
					index={index}
					dragItem={dragItem}
					onDragItemChange={(newItem) => {
						setDragItems([...dragItems.slice(0, index), newItem, ...dragItems.slice(index + 1)]);
					}}
					renderItem={(item, i) => renderItem(item, i, allDataItems, draggingData)}
					renderSeparator={
						renderSeparator == null ?
							null :
							(item, i) => renderSeparator(item, i, allDataItems, draggingData)
					}
					onMove={moveItem}
					onDrop={() => onOrderChange(dragItemsToDataItems(dragItems), dragItem.data, index)}
					getCanDrag={getCanDrag}
					includeSeparator
				/>
			))}
		</div>
	);
};

export default DraggableList;
